/**
 * @fileoverview Shared definitions for narrative elements.
 *
 * Single source of truth for the *domain knowledge* used across storyline,
 * plot thread, character arc, and backstory agents — what each element IS,
 * how to identify it, and how it differs from its neighbours.
 *
 * Companion file `story-type-density.ts` covers the *quantitative* per-
 * story-type guidance (how many beats, how many storylines, etc.).
 *
 * Each constant is a markdown template string designed to be inlined into
 * prompt template literals.
 */

// =============================================================================
// STRUCTURE & PHASES
// =============================================================================

/**
 * The 7-phase structural model used across all storyline agents.
 */
export const STRUCTURE_PHASES = `\
### Structure Phases (7-Phase Model)

Every storyline unfolds through key structural moments:

| Phase | Act | Description |
|-------|-----|-------------|
| **Initial State** | Act 1 | The status quo before disruption. Shows the "normal" that will be transformed. |
| **Inciting Incident** | Act 1 | The event that kicks the story into motion. Something happens that demands response. |
| **Break Into Two** | Act 1 | The commitment. The protagonist enters unfamiliar territory. No going back. |
| **Midpoint** | Act 2 | A major event shifts protagonist from reactive to proactive. Stakes are raised. |
| **Break Into Three** | Act 2 | The lowest point. Everything seems lost. The seed of the solution emerges from crisis. |
| **Climax** | Act 3 | The protagonist faces the central conflict. All storylines converge. Defining choice. |
| **Final State** | Act 3 | The new equilibrium. Shows how the world has changed. Contrasts with Initial State. |`;

/**
 * The dual-phase model (globalPhase vs storylinePhase) explanation.
 */
export const DUAL_PHASE_MODEL = `\
### Dual-Phase Model (globalPhase vs storylinePhase)

Each beat carries TWO phase assignments:

- **globalPhase**: Where the beat sits in the OVERALL story timeline. The A-Story defines this timeline — its phases are the "columns" of the story matrix.
- **storylinePhase**: What structural function the beat serves for THIS STORYLINE's internal arc. A storyline's own "Inciting Incident" might occur at the global "Midpoint" if that storyline starts late.

**Rules:**
- For **A-storylines**: globalPhase ALWAYS equals storylinePhase (the A-Story IS the global timeline).
- For **B/C-storylines**: storylinePhase may differ from globalPhase. A B-story that begins at the global Midpoint would have its first beat with globalPhase="Midpoint" but storylinePhase="Initial State".
- Think of globalPhase as "when" and storylinePhase as "what role in this thread's arc."`;

// =============================================================================
// STORYLINE — THE OVERARCHING NARRATIVE SPINE
// =============================================================================

/**
 * Definition of a Storyline. Parallels PLOT_THREAD_DEFINITION,
 * CHARACTER_ARC_DEFINITION, and BACKSTORY_DEFINITION — focus / key
 * question / identification rule / examples / "NOT" list — and embeds the
 * per-field output guidance (name, summary) at the bottom so a single
 * import covers the full domain concept.
 *
 * For the A/B/C category distinctions, see ABC_CATEGORIES below.
 */
export const STORYLINE_DEFINITION = `\
### Storylines — The Overarching Narrative Spines

A Storyline is a primary narrative thread that runs across the entire story, tracked as a sequence of beats against the 7-phase structural model. Storylines are the spines that the rest of the narrative hangs from — plot threads weave through them, character arcs run alongside them, and backstory feeds into them.

- **Focus**: A sustained external conflict, thematic/emotional journey, or independent concurrent subplot that progresses across the full story timeline
- **Key Question**: "What sustained narrative thread runs across the whole story and resolves by the end?"
- **Identification Rule (The Full-Arc Test)**: A storyline must have its own beginning, middle, and end mapped to the 7-phase structure. If it doesn't span at least most of the story arc, it's a plot thread, a character beat, or backstory — not a storyline.

**Examples**:
- "The Ark Hunt" (A-storyline) — Indy's external race against the Nazis across the full film
- "Indy & Marion" (B-storyline) — Their reconnection arc, weaving emotional stakes through the action
- "Padan Fain's Pursuit" (C-storyline) — An antagonist's independent tracking arc running parallel to the main party

**Storylines are NOT**:
- Plot Threads — localized narrative sequences (heists, escapes, mysteries) that operate within or across storylines
- Character Arcs — individual psychological transformations that track internal change rather than external/thematic narrative threads
- Backstory — events completed before the primary timeline begins
- Single scenes or one-off events that don't develop across the structural phases

Each storyline has:
- **Name**: A short, evocative title (3-4 words). Category-specific patterns:
  - A-storylines: Named after the main physical goal or conflict ("The Ark Hunt", "Survival in the Arena", "The Bennet Marriage Quest")
  - B-storylines: Named after the thematic/emotional journey ("Trusting the Force", "The Katniss-Peeta Romance", "Faith vs. Hubris")
  - C-storylines: Named after the concurrent independent subplot ("Padan Fain's Pursuit", "Imperial Boardroom Politics", "Charlotte's Pragmatic Marriage")
- **Priority**: A, B, or C — see Storyline Priorities for category definitions.
- **Summary**: 3-4 sentences capturing how the storyline starts, develops, and resolves at a high level. The summary should give a quick overview of the full arc without needing to read individual beats.
  - Example: "Indiana Jones races to find the Ark of the Covenant before the Nazis, driven by both professional ambition and a desire to keep the artifact out of dangerous hands. After surviving traps and betrayals across multiple continents, he secures the Ark but discovers its power is beyond anyone's control. The Ark is ultimately locked away by the government, leaving Indy victorious but unsatisfied."
- **Beats**: Structural moments mapped across the 7-phase model (see Dual-Phase Model for how globalPhase and storylinePhase interact).
- **Story Questions**: Dramatic questions whose unanswered state hooks the reader across the arc.`;

// =============================================================================
// A/B/C CATEGORY DISTINCTIONS
// =============================================================================

/**
 * A/B/C category distinctions — the priority/role of each storyline.
 *
 * This constant defines what differentiates the three categories. The
 * *quantity* of each category for a given story length lives in the
 * STORYLINE_COUNT_* constants (see story-type-density.ts) and is rendered
 * via the `[[[storylineCountGuidance]]]` branching macro.
 *
 * A-Story sourced from a-storyline-extractor-v1.
 * B-Story sourced from b-storyline-extractor-v1 (incl. "B-Stories are NOT").
 * C-Story sourced from c-storyline-extractor-v1 (incl. Independence Test).
 */
export const ABC_CATEGORIES = `\
### Storyline Priorities (A/B/C Categories)

A/B/C are **priority categories**, not fixed slots. Each category plays a different structural role in the narrative.

**A-Storylines — The External Spine** (at least 1 required): The primary external plot and central driving force of the narrative. It defines the overarching objective, the main conflict, and the physical stakes.
- Focus: Events, actions, and the ultimate goal (e.g., defeating the villain, surviving the disaster, winning the championship)
- Central question: "What physical goal are the protagonists trying to accomplish?"
- **Identification Rule**: If you were to write a three-sentence summary (logline) of the book or movie, you are describing the A-Story
- Always about **external action and stakes**, not internal/emotional development (that's B-Story territory)
- Examples: "The Ark Hunt" (Indy races to find the Ark before the Nazis), "The Rebellion" (rebels must destroy the Death Star), "Survival in the Arena" (Katniss fights to survive the 74th Hunger Games)

**B-Storylines — The Internal/Thematic Spine** (at least 1 required): The main emotional, relational, or thematic arc(s) of the story. Runs parallel to the A-Story and explores the underlying meaning or interpersonal dynamics of the narrative. Without B-Stories, the A-Story's events would be hollow action with no meaning.
- Focus: Broad themes, romances, shifting alliances, and moral debates (e.g., faith vs. logic, the dangers of hubris, the power of found family)
- Central question: "What is this story actually about underneath the action?"
- **Identification Rule**: A B-Story is a shared thematic or emotional journey experienced by one or more characters
- They are **relational or internal** — about how characters connect emotionally, not about external plot mechanics
- They give the protagonist **emotional stakes** beyond the A-Story's external stakes
- They often represent what the protagonist **needs** (vs. the A-Story which is what they **want**)
- They have their own arc: a relationship that starts broken/absent and becomes whole, or starts whole and fractures
- They typically resolve just before or during the A-Story's climax, providing the protagonist with the emotional insight needed to face the final challenge
- Examples: "Indy & Marion", "Trusting the Force" (faith vs. technology), "The Katniss-Peeta Romance" (fabricated-turned-genuine)
- **B-Stories are NOT**: Minor side quests or logistics (those are C-Stories), the A-Story viewed from a different angle, every relationship in the story — only the ones with genuine emotional arcs, or individual character psychology (that's Character Arcs)

**C-Storylines — The Concurrent Subplot** (optional): Secondary storylines, antagonist maneuvers, or background events that actively unfold parallel to the main timeline. They add breadth, texture, and relief to the world.
- Focus: Side characters, independent factions, or off-screen events that flesh out the broader world and create converging plotlines
- Central question: "What else is actively happening in the world right now?"
- **Identification Rule (The Independence Test)**: A C-Story must consist of external actions occurring concurrently with the main plot. It must still make logical sense and be able to occur even if some of the primary protagonists are entirely removed from the narrative
- Can follow a minor character through their own arc, track an independent faction's movements, explore background political intrigue, or cover antagonist maneuvers happening off-page
- Examples: "Padan Fain's Pursuit" (independent tracking across the continent), "Imperial Boardroom Politics" (Tarkin dissolves the Senate), "Haymitch's Sponsor Politicking" (working Capitol citizens off-page), "Charlotte Lucas's Pragmatic Marriage" (operating with her own agency concurrently)
- **C-Stories are NOT**: Plot Threads (which are localized narrative sequences within the A/B/C stories), minor one-off events, or the protagonist's own logistics

The total number of storylines in each category should scale with the story's length and complexity — see the Storyline Count guidance for specific targets.`;

// =============================================================================
// STORYLINE BEAT FIELD REQUIREMENTS
// =============================================================================

/**
 * The per-field structure of a storyline beat. Shared across all
 * storyline-producing agents (planner, ideation, finder, expander) so the
 * field semantics are described in one place.
 *
 * Per-story-type *quantity* (how many beats, how many beats per phase) lives
 * in the STORYLINE_BEAT_GUIDANCE_* constants in `story-type-density.ts` and
 * is rendered via the `[[[storylineBeatGuidance]]]` branching macro.
 *
 * Agents that emit beats for multiple storylines in a single output (the
 * planner) additionally require a `storylineIndex` field — they should
 * mention that requirement alongside this block.
 */
export const STORYLINE_BEAT_REQUIREMENTS = `\
### Beat Field Requirements

Each beat must specify the following fields:

1. **storylinePhase**: The structural function for this storyline's arc (one of the 7 phases — see Structure Phases above)
2. **globalPhase**: The position in the overall story timeline (equals storylinePhase for A-stories; may differ for B/C — see the Dual-Phase Model above)
3. **order**: Sequencing index for multiple beats at the same globalPhase (0 for a single beat; 0, 1, 2, … for multiple)
4. **plotEvent** (REQUIRED): The concrete, observable event — what physically happens (actions, discoveries, confrontations)
   - Examples: "The rebels destroy the shield generator", "Hero finds the hidden map"
5. **narrativeState** (OPTIONAL): The emotional or thematic state at this moment
   - Examples: "Hope is rekindled", "Trust is shattered", "The mystery deepens"
6. **goalProgress**: Progress toward the storyline's goal, on a scale from -1 to +1
   - +1 = Major victory, breakthrough
   - -1 = Significant setback, defeat
   - 0 = Neutral, setup, lateral movement
   - Typical pattern: Setbacks at Midpoint and Break Into Three, victory at Climax

**Multiple beats per phase:** A single globalPhase can contain multiple beats when the story has multiple significant events at that point. Use the \`order\` field to sequence them. Refer to the Beat Density table for per-phase maximums.`;

// =============================================================================
// OTHER NARRATIVE ELEMENTS — PLOT THREADS, CHARACTER ARCS, BACKSTORY
// =============================================================================

/**
 * Guidance distinguishing Plot Threads from storylines and character arcs.
 * Used by extraction and planning agents that handle plot threads.
 */
export const PLOT_THREAD_DEFINITION = `\
### Plot Threads — The Narrative Sequence

A Plot Thread is a coherent building block and narrative vehicle of the story that actively move the overarching storylines forward.

Plot Threads are a sequences, events, scenarios, episodes, or tactical objectives that the characters must navigate, such as:
1. **Action Sequences**: Heists, escapes, battles, chases, rescues
2. **Social Scenarios**: Gatherings, negotiations, confrontations
3. **Mysteries/Investigations**: Localized puzzles that reveal important information
4. **Survival Episodes**: Perilous journeys, traps, challenges

**Identification Rule**: Plot Threads are the functional "gears" of a story. They operate on a smaller scale with a distinct beginning, middle, and end within the larger work. They often combine different storylines and character arcs in a narrative/plot vehicle.

**Examples**:
- "The Shadar Logoth Escape" — In "The Wheel of Time", the traveling party is forced into a ruined city, Mashadar rises, and the group is violently shattered as a result.
- "The Death Star Rescue" — IN "Star Wars", Luke, Han, and Chewie execute a chaotic rescue of Princess Leia from the detention block on the Death Star.
- "The Tracker Jacker Attack" — I "The HUnger Games", Katniss is trapped in a tree, drops a wasp nest on the Careers and secures a bow.

**SCOPE CONSIDERATIONS**
Plot threads can appear in different ways. Most often, a plot thread will be spread over two or three chapters. It is also possible (but rarer) that a plot thread is handled in exactly one chapter. It is also possible (but even rarer) that a plot thread appears across many chapters.

**Plot Threads are NOT**:
- Full storylines (A/B/C) — those are the overarching narrative spines, plot threads are localized and often go across storylines
- Character arcs — those track internal psychological transformation across the whole story, plot threads are localized and often combine storylines and character arcs.
- Backstory — those are events completed before the primary timeline begins
- Single-scene events that don't develop or recur

Each thread has:
- **Title**: A short, evocative name (2-5 words)
- **Thread Question**: The main dramatic question driving reader engagement. As long as this question is unanswered, the reader is hooked.
- **Summary**: What happens in this thread — this is the context that makes the question meaningful (1-3 sentences)
- **Character Names**: Characters directly involved in this thread`;

/**
 * Guidance distinguishing Character Arcs from storylines and plot threads.
 * Used by character arc extraction and creation agents.
 */
export const CHARACTER_ARC_DEFINITION = `\
### Character Arcs — The Internal Transformation

A Character Arc is the deeply personal, internal psychological and emotional development of a single specific character over the course of the story.

- **Focus**: A character's shifting worldview, the conquering of a flaw, or the processing of an emotion
- **Key Question**: "How does this individual's mindset fundamentally change from the beginning to the end?"
- **Identification Rule (The Internal Change Test)**: A Character Arc must not be a list of physical events. It must strictly map an internal psychological shift

**Examples**:
- "Han Solo: The Birth of a Conscience" — Han shifts from cynical isolationist to self-sacrificing hero
- "Rand al'Thor: The Death of Denial" — Rand's internal psychological surrender to his terrifying new identity
- "Elizabeth Bennet: The Humbling of Prejudice" — Her cognitive shift from infallible first impressions to recognizing her own vanity
- "Dr. Alan Grant: Reluctant Fatherhood" — Grant's shift from disdain for children to embracing paternal instincts

**Character Arcs are NOT**:
- Lists of physical events or plot points (those belong to storylines and plot threads)
- B-Stories (which are shared thematic/relational journeys, not individual psychological maps)
- Backstory (which is completed before the primary timeline begins)`;

/**
 * Guidance for Backstory — historical context that predates the primary timeline.
 * Used by extraction and planning agents.
 */
export const BACKSTORY_DEFINITION = `\
### Backstory — The Historical Context

Backstory consists of formative events, historical lore, or past traumas that occurred entirely before the primary timeline (A-Story) begins.

- **Focus**: Pre-existing conditions, past relationships, or world-building history that dictate the starting state of the narrative
- **Key Question**: "What happened in the past that created the current conflict or the characters' initial flaws?"
- **Identification Rule (The Time Test)**: If the event is already completed before Chapter 1 / Scene 1, it is Backstory

**Examples**:
- "The Breaking of the World" — Thousands of years ago, the male half of the magic source was tainted, destroying the world
- "The Fall of the Jedi" — Decades before the film, the Jedi were wiped out and Anakin Skywalker fell to the dark side
- "Mr. Everdeen's Death" — Five years prior, Katniss's father died in a mine explosion, creating her foundational trauma
- "Mr. Wickham's Attempted Elopement" — Years prior, Wickham attempted to elope with Darcy's 15-year-old sister
- "Hammond's Flea Circus" — Decades ago, Hammond ran a flea circus, establishing his lifelong obsession with control

**Backstory is NOT**:
- Active events in the primary timeline (those are storylines or plot threads)
- Character arcs (which track transformation during the story, not before it)
- World-building exposition that doesn't drive the starting conflict`;

/**
 * Combined differentiation guide showing how all six narrative element types
 * relate to each other. Used in extraction agents to prevent misclassification.
 */
export const NARRATIVE_ELEMENT_DIFFERENTIATION = `\
### Differentiating Narrative Elements

When classifying story elements, use these tests to determine the correct type:

| Element | Scale | Timeline | Focus | Test Question |
|---------|-------|----------|-------|---------------|
| **A-Story** | Full narrative | Present | External goals & stakes | "What's the logline?" |
| **B-Story** | Full narrative | Present | Emotional/thematic meaning | "What's it really about?" |
| **C-Story** | Partial narrative | Present (concurrent) | Independent parallel events | "Does it work without the protagonist?" |
| **Plot Thread** | Localized sequence | Present | Specific scenario/episode | "What scenario are they navigating now?" |
| **Character Arc** | Full narrative | Present (internal) | Individual psychological shift | "How does this person's mind change?" |
| **Backstory** | Pre-narrative | Past (completed) | Historical context | "Was this done before Chapter 1?" |

**Common misclassifications to avoid:**
- A protagonist's internal journey is a **Character Arc**, not a B-Story (B-Stories are shared thematic/relational journeys)
- A heist sequence or battle is a **Plot Thread**, not a C-Story (C-Stories are independent concurrent subplots)
- A character's tragic past is **Backstory**, not a Character Arc (arcs track transformation during the story)
- An antagonist's off-screen maneuvers are a **C-Story**, not a Plot Thread (plot threads are localized scenarios the characters actively navigate)`;
